import { Component, computed, effect, inject, Signal, signal, WritableSignal } from '@angular/core';
import { Toolbar } from 'primeng/toolbar';
import { MenuItem } from 'primeng/api';
import { Image } from 'primeng/image';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Toast } from 'primeng/toast';
import { Divider } from 'primeng/divider';
import { Chip } from 'primeng/chip';
import { Button } from 'primeng/button';
import { Menu } from 'primeng/menu';
import { NgClass } from '@angular/common';
import { Ripple } from 'primeng/ripple';
import { Router } from '@angular/router';
import { Auth, Bootstrap } from '@synegen/utility';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ImageFallbackDirective, ProfileImageService } from '@synegen/file';

@Component({
  selector: 'ems-header',
  imports: [
    Toolbar,
    Image,
    Toast,
    Divider,
    Chip,
    Button,
    TranslatePipe,
    Menu,
    NgClass,
    Ripple,
    ImageFallbackDirective
  ],
  templateUrl: './header.html',
  styleUrl: './header.scss',
  standalone: true
})
export class Header {
  menuItems: WritableSignal<CustomMenuItem[]> = signal([]);
  userMenuItems: WritableSignal<MenuItem[]> = signal([]);
  adminMenuItems: WritableSignal<MenuItem[]> = signal([]);

  #profileImageService: ProfileImageService = inject(ProfileImageService);
  translate: TranslateService = inject(TranslateService);
  auth: Auth = inject(Auth);

  #router: Router = inject(Router);
  #http: HttpClient = inject(HttpClient);
  #bootstrap: Bootstrap = inject(Bootstrap);

  title: Signal<string> = computed(() => <string>this.#bootstrap.get('title'));
  image = computed(() => this.#profileImageService.imageUrl());

  constructor() {
    effect(() => {
      const imageId = this.#bootstrap.get('user.imageId');
      const imageUrl = imageId ? `${environment.apiContext}/file/${imageId}/content` : '';
      this.#profileImageService.setImageUrl(imageUrl); // was: only called when imageId was truthy

      const productMenu = <CustomMenuItem[] | undefined>this.#bootstrap.get('productMenu.content');
      if (productMenu) {
        productMenu.forEach((p) => {
          const rawUrl = p.url ?? '';

          // Same-tab in-app route: /abc
          if (rawUrl.startsWith('/')) {
            p.url = rawUrl;
            delete p.target;
            return;
          }

          // Hack for routes that will eventually be external but are spoofed by our site: ://abc
          if (rawUrl.startsWith('://')) {
            const route = '/' + rawUrl.replace('://', '');

            p.command = () => {
              const url = window.location.origin + route;
              window.open(url, '_blank', 'noopener,noreferrer');
            };

            delete p.url;
            delete p.routerLink;
            delete p.target;
            return;
          }

          // External URL
          if (rawUrl.includes('://')) {
            p.command = () => {
              window.open(rawUrl, '_blank', 'noopener,noreferrer');
            };

            delete p.url;
            delete p.routerLink;
            delete p.target;
          }
        });

        this.menuItems.set(productMenu);
      } else {
        this.menuItems.set([]); // was missing — left the previous context's menu showing indefinitely
      }

      this.userMenuItems.set(this.#buildUserMenu());
      const adminMenuItems: MenuItem[] = <MenuItem[]>this.#bootstrap.get('adminMenu.content') ?? [];
      adminMenuItems.forEach((ami) => (ami.target = '_self'));
      this.adminMenuItems.set(
        this.auth.hasAuthority('system:role:admin') ? (adminMenuItems ?? []) : []
      );
    });
  }

  logout = () => {
    void this.#router.navigate(['/logout']);
  };

  go = (path: string) => {
    void this.#router.navigate([path]);
  };

  toggleFavorite = (event: Event, item: CustomMenuItem) => {
    event.stopPropagation();
    event.preventDefault();

    this.#http.put<any>(`favorites/ROUTE/${item.id}/toggle`, {}).subscribe({
      next: () => {
        this.menuItems.update((items) => this.#toggleFavoriteInMenu(items, item.id));
      },
      error: (errorResponse: HttpErrorResponse) => {
        console.error(errorResponse);
      }
    });
  };

  #toggleFavoriteInMenu(items: CustomMenuItem[], id: number): CustomMenuItem[] {
    return items.map((item) => {
      const children = item.items
        ? this.#toggleFavoriteInMenu(item.items as CustomMenuItem[], id)
        : undefined;

      if (item.id === id) {
        return {
          ...item,
          favorited: !item.favorited,
          items: children ?? item.items
        };
      }

      return {
        ...item,
        items: children ?? item.items
      };
    });
  }

  #buildUserMenu(): MenuItem[] {
    const items: MenuItem[] = [];
    const dbItems: MenuItem[] = this.#bootstrap.get('userMenu.content') ?? [];
    if (dbItems.length > 0) {
      items.push(...dbItems);
    }
    items.push({
      label: 'My Profile',
      icon: 'pi pi-user',
      command: () => void this.#router.navigateByUrl('my-profile')
    });
    items.push({
      label: 'Logout',
      icon: 'pi pi-sign-out',
      command: () => this.logout()
    });
    const isStepped = this.#bootstrap.get('steppedTenant');
    if (isStepped) {
      const tenant = this.#bootstrap.get<{ displayName: string; tenantKey: string }>('tenant');
      if (!!tenant) {
        items.push({
          label: `Exit ${tenant.tenantKey}`,
          icon: 'pi pi-sign-out',
          command: () => this.popContext()
        });
      }
    }
    return items;
  }

  popContext = () => {
    this.#http.post('tenant/step-out', {}).subscribe({
      next: () => {
        this.#bootstrap.load().subscribe(() => {
          this.#router.navigateByUrl('/').then(() => location.reload());
        });
      },
      error: (errorResponse: HttpErrorResponse) => {
        console.error(errorResponse.error);
      }
    });
  };
}

type CustomMenuItem = MenuItem & {
  id: number;
  favorited?: boolean;
  items?: CustomMenuItem[];
};
